import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { PositionData } from '@interfaces/open-positions.model';
import { OpsOpenPositionsService } from './ops-open-positions.service';
import { ColumnDefParams } from '@emerald/nxcore';
import { columnDefs } from './ops-openpositions.config';
import { GlobalErrorService } from '@services/global-error.service';
import { SearchService } from '@services/search.service';
import { FileDownloadService } from '@services/file-download.service';
import { Candidates } from '@interfaces/candidates.model';
import { PersonaType, TabLabels, Priority } from '@constants/global.enum';
import { mapResponse } from './ops-open-positions.helper';
import { OpsCandidatesService } from '../ops-candidates/ops-candidates.service';
import { interval, takeWhile } from 'rxjs';
import { dateFormat } from '@constants/dateFormat';
import { truncateText } from '@utils/utils';

@Component({
  selector: 'app-ops-open-positions',
  templateUrl: './ops-open-positions.component.html',
  styleUrls: ['./ops-open-positions.component.scss'],
})
export class OpsOpenPositionsComponent {
  fileUrl: string = 'https://pdfobject.com/pdf/sample.pdf';
  columnDefs: ColumnDefParams<any>[] = columnDefs;
  openPositions: PositionData[] = [];
  displayData: PositionData[] = [];
  positionCount = 0;
  activeTabLabel: string = TabLabels.OpenPositions;
  errorMessage: string | null = null;
  candidatesCount = 0;
  imageSrc: string = 'assets/img/no-data-found.png';
  displayCandidatesData: Candidates[] = [];
  fieldsVisibility: string = PersonaType.OpsTeamOpenPos;
  filterValue: string = '';
  tabLabel = TabLabels;
  priority = Priority;
  isDataLoaded: boolean = false;
  isCandidateDataLoaded: boolean = false;
  dateFormat = dateFormat;
  @Input() tooltipDelayShow = 500;
  tooltipDelayHide = 200;
  columnTextDisplayLimit = 8;
  offset: [number, number] = [0, 8];
  public truncateText = truncateText;
  showJobDetails = false;
  selectedJob: PositionData | null = null;

  constructor(
    private router: Router,
    private openPositionsService: OpsOpenPositionsService,
    private errorHandler: GlobalErrorService,
    private searchService: SearchService,
    private fileDownloadService: FileDownloadService,
    private cdr: ChangeDetectorRef,
    private candidatesService: OpsCandidatesService,
  ) {}

  ngOnInit() {
    this.loadOpenPositions();
    this.getCandidatesCount();
  }

  private loadOpenPositions(): void {
    this.openPositionsService.getOpenPositions();
    // Loads open positions by periodically fetching data until the data is fully loaded.
    interval(1000)
      .pipe(takeWhile(() => !this.isDataLoaded))
      .subscribe({
        next: () => {
          this.fetchOpenPositions();
        },
        error: (error) => {
          this.isDataLoaded = true;
          this.errorHandler.logComponentError(error);
        },
      });
  }
  selectTab(label: string): void {
    if (label === this.activeTabLabel) return;
    this.activeTabLabel = label;
    if (label === TabLabels.OpenPositions) {
      this.isDataLoaded = false;
      this.openPositionsService.openPositionsSignal.set(null);
      this.loadOpenPositions();
    }
  }

  // private fetchOpenPositions(): void {
  //   const signalData = this.openPositionsService.openPositionsSignal();
  //   if (signalData && signalData?.data) {
  //     this.openPositions = signalData?.data?.map(mapResponse);
  //     this.displayData = this.openPositions || [];
  //     this.getCandidatesCount();
  //     this.getContPositions();
  //     this.isDataLoaded = true;
  //   }
  // }
  private fetchOpenPositions(): void {
    const signalData = this.openPositionsService.openPositionsSignal();
    if (signalData && signalData?.data) {
      this.openPositions = signalData?.data?.map(mapResponse);
      this.displayData = this.openPositions || [];
      this.getContPositions();
      this.pageIndex = 1;
      this.isDataLoaded = true;
    }
  }

  getCandidatesCount() {
    this.candidatesService.getCandidates(1, 10);
    interval(1000)
      .pipe(takeWhile(() => !this.isCandidateDataLoaded))
      .subscribe({
        next: () => {
          const candidateSignalData = this.candidatesService.candidatesSignal();
          if (candidateSignalData && candidateSignalData?.data) {
            this.candidatesCount = candidateSignalData.data.totalRecords;
            this.isCandidateDataLoaded = true;
          }
        },
        error: (error) => {
          this.isCandidateDataLoaded = true;
          this.errorHandler.logComponentError(error);
        },
      });
  }

  searchItem(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(event);
    this.displayData =
      this.openPositions && this.openPositions.length
        ? this.searchService.getSearchedData(
            this.openPositions,
            this.filterValue?.toLowerCase(),
          )
        : [];
    this.getContPositions();
    this.pageIndex = 1;
    this.cdr.detectChanges();
  }

  clearInput() {
    this.displayData = this.openPositions;
    this.filterValue = '';
    this.getContPositions();
    this.pageIndex = 1;
  }

  getContPositions() {
    this.positionCount = this.searchService.getContPositions(
      this.displayData,
      null,
    );
  }

  raiseHirinRequest() {
    this.router.navigate(['hiringrequest']);
  }
  handleDataFromChild(data: PositionData[]) {
    if (!data) {
      console.error('Received null or undefined data from child');
      return;
    }
    this.displayData = data;
    this.filterValue = '';
    this.getContPositions();
    this.pageIndex = 1;
  }

  getVendorName(row: any): string {
    return typeof row?.vendorName === 'string'
      ? row?.vendorName
      : String(row?.vendorName);
  }

  downloadFile(filePath: string, fileName: string, fileType: string) {
    const fullFileName = fileType + '_' + fileName + '.pdf';
    this.fileDownloadService.downloadFile(filePath, fullFileName);
  }

  navigateToJobDetails(data: PositionData) {
    this.selectedJob = data;
    this.showJobDetails = true;
  }

  onCandidatesCountChange(count: number): void {
    this.candidatesCount = count;
  }

  // ── op-table: client-side sort + pagination ──────────────────────────
  sortField: string = 'createdDate';
  sortDir: 'asc' | 'desc' = 'desc';
  pageIndex = 1;
  pageSize = 10;

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
    this.pageIndex = 1;
  }

  get sortedData(): any[] {
    const data = [...(this.displayData || [])];
    const f = this.sortField;
    if (!f) return data;
    data.sort((a: any, b: any) => {
      const av = a?.[f];
      const bv = b?.[f];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, {
              numeric: true,
              sensitivity: 'base',
            });
      return this.sortDir === 'asc' ? cmp : -cmp;
    });
    return data;
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil((this.displayData?.length || 0) / this.pageSize),
    );
  }

  get pagedData(): any[] {
    const start = (this.pageIndex - 1) * this.pageSize;
    return this.sortedData.slice(start, start + this.pageSize);
  }

  get rangeStart(): number {
    return (this.displayData?.length || 0) === 0
      ? 0
      : (this.pageIndex - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(
      this.pageIndex * this.pageSize,
      this.displayData?.length || 0,
    );
  }

  /** Page numbers to render; -1 is an ellipsis gap. */
  get pageWindow(): number[] {
    const total = this.totalPages;
    const cur = this.pageIndex;
    const out: number[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) out.push(i);
      return out;
    }
    out.push(1);
    const start = Math.max(2, cur - 1);
    const end = Math.min(total - 1, cur + 1);
    if (start > 2) out.push(-1);
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push(-1);
    out.push(total);
    return out;
  }

  goToPage(page: number): void {
    if (
      page === -1 ||
      page < 1 ||
      page > this.totalPages ||
      page === this.pageIndex
    )
      return;
    this.pageIndex = page;
  }

  prevPage(): void {
    this.goToPage(this.pageIndex - 1);
  }

  nextPage(): void {
    this.goToPage(this.pageIndex + 1);
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = +(event.target as HTMLSelectElement).value;
    this.pageIndex = 1;
  }

  trackByIndex(index: number): number {
    return index;
  }

  statusTone(status: string | null | undefined): string {
    const s = (status || '').toLowerCase();
    if (s.includes('open')) return 'open';
    if (s.includes('hold')) return 'hold';
    if (s.includes('clos')) return 'closed';
    return 'closed';
  }
}
